import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, RotateCcw } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import UndoConfirmDialog from "./UndoConfirmDialog";

interface BulkImportExpandedRowProps {
  batchId: string;
  batchStatus: string;
  locationId: string;
  organizationId: string;
  importType?: string;
  /**
   * Offer Undo per SKU (the add-stock view). False for a Base garments upload:
   * its stock is undone as a whole (undoBulkStocktake), never per SKU.
   */
  skuUndo?: boolean;
}

type VarianceDirection = "all" | "short" | "over";

const csvEscape = (value: unknown) => {
  const str = value == null ? "" : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const BulkImportExpandedRow = ({
  batchId,
  batchStatus,
  locationId,
  importType,
  skuUndo = true,
}: BulkImportExpandedRowProps) => {
  const { format } = useCurrencyFormatter();
  const { toast } = useToast();
  const utils = trpc.useUtils();

  const isStocktake = importType === "stocktake";

  const [direction, setDirection] = useState<VarianceDirection>("all");
  const [hideUnchanged, setHideUnchanged] = useState(true);

  const [undoSkuTarget, setUndoSkuTarget] = useState<{
    skuId: string;
    sku: string;
    quantity: number;
  } | null>(null);

  const detailQuery = trpc.logs.getBulkImportBatchDetail.useQuery(
    { batchId, locationId },
    { staleTime: 30_000 }
  );

  const undoMutation = trpc.inventory.undoBulkImport.useMutation({
    onSuccess: () => {
      toast({ title: "Undo successful", description: "Stock changes have been reversed." });
      void utils.logs.getBulkImportBatches.invalidate();
      void utils.logs.getBulkImportBatchDetail.invalidate({ batchId, locationId });
      void utils.logs.getStockMovements.invalidate();
      setUndoSkuTarget(null);
    },
    onError: (error) => {
      toast({
        title: "Undo failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleUndoSku = () => {
    if (!undoSkuTarget) return;
    undoMutation.mutate({
      locationId,
      batchId,
      skuId: undoSkuTarget.skuId,
    });
  };

  const rows = useMemo(() => detailQuery.data ?? [], [detailQuery.data]);

  // Stocktake variance summary — expected (stock before) vs counted (stock after).
  const summary = useMemo(() => {
    let varied = 0;
    let over = 0;
    let short = 0;
    let netUnits = 0;
    let netValue = 0;
    let overValue = 0;
    let shortValue = 0;
    for (const r of rows) {
      const qty = r.quantityChange;
      const val = r.costChangeUsd ?? 0;
      netUnits += qty;
      netValue += val;
      if (qty > 0) {
        over += 1;
        overValue += val;
      } else if (qty < 0) {
        short += 1;
        shortValue += val;
      }
      if (qty !== 0) varied += 1;
    }
    return {
      counted: rows.length,
      varied,
      over,
      short,
      netUnits,
      netValue,
      overValue,
      shortValue,
    };
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!isStocktake) return rows;
    return rows.filter((r) => {
      if (hideUnchanged && r.quantityChange === 0) return false;
      if (direction === "over") return r.quantityChange > 0;
      if (direction === "short") return r.quantityChange < 0;
      return true;
    });
  }, [rows, isStocktake, hideUnchanged, direction]);

  const handleExportCsv = () => {
    const header = [
      "SKU",
      "Title",
      "Size",
      "Colour",
      "Expected",
      "Counted",
      "Variance",
      "Value Change (USD)",
    ];
    const lines = visibleRows.map((r) =>
      [
        r.sku,
        r.title,
        r.size,
        r.color,
        r.stockBefore,
        r.stockAfter,
        r.quantityChange,
        r.costChangeUsd ?? "",
      ]
        .map(csvEscape)
        .join(",")
    );
    const csv = [header.join(","), ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `stocktake-variance-${batchId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (detailQuery.isLoading) {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground">
        Loading details...
      </div>
    );
  }

  if (detailQuery.error) {
    return (
      <div className="py-4 text-center text-sm text-destructive">
        {detailQuery.error.message}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground">
        No transaction details found for this batch.
      </div>
    );
  }

  const isFullyUndone = batchStatus === "undone";

  const moneyClass = (n: number) =>
    n > 0
      ? "text-emerald-700 dark:text-emerald-400"
      : n < 0
        ? "text-red-700 dark:text-red-400"
        : "text-muted-foreground";

  // ---------- Stocktake variance report ----------
  if (isStocktake) {
    return (
      <div className="space-y-3 py-2">
        {/* Summary */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-md border bg-background p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">SKUs varied</div>
            <div className="text-lg font-semibold tabular-nums">
              {summary.varied.toLocaleString()}
              <span className="text-sm font-normal text-muted-foreground"> / {summary.counted.toLocaleString()}</span>
            </div>
          </div>
          <div className="rounded-md border bg-background p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Net variance</div>
            <div className={`text-lg font-semibold tabular-nums ${moneyClass(summary.netValue)}`}>
              {summary.netValue > 0 ? "+" : ""}{format(summary.netValue)}
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">
              {summary.netUnits > 0 ? "+" : ""}{summary.netUnits.toLocaleString()} units
            </div>
          </div>
          <div className="rounded-md border bg-background p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Surplus (found)</div>
            <div className="text-lg font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
              {format(summary.overValue)}
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">{summary.over} SKUs</div>
          </div>
          <div className="rounded-md border bg-background p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Shrink (missing)</div>
            <div className="text-lg font-semibold tabular-nums text-red-700 dark:text-red-400">
              {format(summary.shortValue)}
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">{summary.short} SKUs</div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5">
            {(["all", "short", "over"] as const).map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={direction === d}
                onClick={() => setDirection(d)}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                  direction === d ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {d === "all" ? "All" : d === "short" ? "Shrink" : "Surplus"}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={hideUnchanged}
              onChange={(e) => setHideUnchanged(e.target.checked)}
            />
            Hide unchanged
          </label>
          <span className="text-xs text-muted-foreground">{visibleRows.length.toLocaleString()} SKUs</span>
          <Button variant="outline" size="sm" className="ml-auto h-7 px-2 text-xs" onClick={handleExportCsv}>
            <Download className="mr-1 h-3 w-3" />
            Export CSV
          </Button>
        </div>

        {/* Variance table */}
        <div className="overflow-hidden rounded-md border bg-background">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">SKU</TableHead>
                <TableHead className="hidden md:table-cell">Title</TableHead>
                <TableHead className="w-[70px] hidden sm:table-cell">Size</TableHead>
                <TableHead className="w-[90px] hidden sm:table-cell">Colour</TableHead>
                <TableHead className="w-[90px] text-right">Expected</TableHead>
                <TableHead className="w-[90px] text-right">Counted</TableHead>
                <TableHead className="w-[100px] text-right">Variance</TableHead>
                <TableHead className="w-[110px] text-right hidden sm:table-cell">$ Impact</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                    No SKUs match this filter.
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row) => (
                  <TableRow key={row.transactionId}>
                    <TableCell>
                      {row.sku ? (
                        <Link
                          to={row.productId ? `/products/${row.productId}` : `/inventory?search=${encodeURIComponent(row.sku)}`}
                          className="font-mono text-sm text-primary hover:underline"
                        >
                          {row.sku}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">Unknown</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-[240px] truncate text-muted-foreground" title={row.title ?? undefined}>
                      {row.title ?? "-"}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{row.size ?? "-"}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{row.color ?? "-"}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{row.stockBefore}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{row.stockAfter}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className={moneyClass(row.quantityChange)}>
                        {row.quantityChange > 0 ? "+" : ""}
                        {row.quantityChange}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-right tabular-nums">
                      {row.costChangeUsd != null ? (
                        <span className={moneyClass(row.costChangeUsd)}>
                          {row.costChangeUsd > 0 ? "+" : ""}
                          {format(row.costChangeUsd)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // ---------- Bulk add-stock import (existing view) ----------
  return (
    <div className="space-y-2 py-2">
      <div className="rounded-md border bg-background overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">SKU</TableHead>
              <TableHead className="w-[100px] text-right">Qty Change</TableHead>
              <TableHead className="w-[120px] text-right hidden sm:table-cell">Unit Cost</TableHead>
              <TableHead className="w-[120px] text-right hidden sm:table-cell">Value Change</TableHead>
              <TableHead className="w-[180px] text-center">Stock Before / After</TableHead>
              {skuUndo && <TableHead className="w-[100px]">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const isNegativeQty = row.quantityChange < 0;
              const isRowUndone = !!row.undoneAt;

              return (
                <TableRow key={row.transactionId}>
                  <TableCell>
                    {row.sku ? (
                      <Link
                        to={row.productId ? `/products/${row.productId}` : `/inventory?search=${encodeURIComponent(row.sku)}`}
                        className="font-mono text-sm text-primary hover:underline"
                      >
                        {row.sku}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">Unknown</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span
                      className={
                        isNegativeQty
                          ? "text-red-700 dark:text-red-400"
                          : isRowUndone
                            ? "text-muted-foreground line-through"
                            : "text-emerald-700 dark:text-emerald-400"
                      }
                    >
                      {row.quantityChange > 0 ? "+" : ""}
                      {row.quantityChange}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums hidden sm:table-cell">
                    {row.unitCostUsd != null ? format(row.unitCostUsd) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums hidden sm:table-cell">
                    {row.costChangeUsd != null ? (
                      <span className={moneyClass(row.costChangeUsd)}>
                        {row.costChangeUsd > 0 ? "+" : ""}
                        {format(row.costChangeUsd)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center tabular-nums">
                    <span className="text-muted-foreground">{row.stockBefore}</span>
                    <span className="mx-1 text-muted-foreground">&rarr;</span>
                    <span className="font-medium">{row.stockAfter}</span>
                  </TableCell>
                  {skuUndo && (
                    <TableCell>
                      {isRowUndone ? (
                        <Badge variant="outline" className="text-xs">
                          Undone
                        </Badge>
                      ) : !isFullyUndone && row.quantityChange !== 0 && row.skuId ? (
                        // Direction-aware: consume rows (negative change) are
                        // reversed by adding the units back — same backend path.
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={undoMutation.isPending}
                          onClick={() =>
                            setUndoSkuTarget({
                              skuId: row.skuId!,
                              sku: row.sku ?? "Unknown",
                              quantity: row.quantityChange,
                            })
                          }
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Undo
                        </Button>
                      ) : null}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <UndoConfirmDialog
        open={undoSkuTarget !== null}
        onConfirm={handleUndoSku}
        onCancel={() => setUndoSkuTarget(null)}
        isLoading={undoMutation.isPending}
        title={`Undo import for ${undoSkuTarget?.sku ?? "this SKU"}?`}
        description={
          (undoSkuTarget?.quantity ?? 0) < 0
            ? `This will add the ${Math.abs(undoSkuTarget?.quantity ?? 0)} consumed units back for ${undoSkuTarget?.sku ?? "this SKU"}.`
            : `This will reverse the stock addition of ${undoSkuTarget?.quantity ?? 0} units for ${undoSkuTarget?.sku ?? "this SKU"}.`
        }
        itemCount={1}
        totalQuantity={Math.abs(undoSkuTarget?.quantity ?? 0)}
      />
    </div>
  );
};

export default BulkImportExpandedRow;
